/**
 * Mission Status Service
 *
 * High-level service for subscribing to mission execution status updates.
 * Wraps WebSocketStreamingClient with mission-specific functionality.
 *
 * Adapted from admin-ui MissionStatusService for chat-ui.
 * Tool generation subscriptions omitted (admin-only feature).
 */

import { WebSocketStreamingClient } from './websocket-client';
import { config } from '@/utils/config';
import { logger } from '@/utils/logger';
import type {
  EscalationAnswerFrame,
  EscalationAnswerResult,
  OperatorMessageView,
  UserMessageView,
} from '@agion/mission-stream';
import type {
  MissionSubscriptionOptions,
  NodeStatusUpdateMessage,
  EdgeActivationMessage,
  AgentSpeakingMessage,
  VoiceRoomReadyMessage,
  AgentTurnMessage,
  ToolCallMessage,
  ToolResultMessage,
  HITLRequestMessage,
  HITLResponseMessage,
  MissionCompletedMessage,
  MissionFailedMessage,
  ConnectionState,
} from '@/types/streaming';

// Re-export types for convenience
export type {
  MissionSubscriptionOptions,
  NodeStatusUpdateMessage,
  EdgeActivationMessage,
  AgentSpeakingMessage,
  VoiceRoomReadyMessage,
  AgentTurnMessage,
  ToolCallMessage,
  ToolResultMessage,
  HITLRequestMessage,
  HITLResponseMessage,
  MissionCompletedMessage,
  MissionFailedMessage,
  ConnectionState,
};

// Singleton instance
let instance: MissionStatusService | null = null;

/**
 * Mission Status Service
 *
 * Provides a singleton interface for subscribing to mission status updates.
 */
export class MissionStatusService {
  private client: WebSocketStreamingClient;
  private isInitialized = false;

  private constructor() {
    const wsUrl = this.buildWebSocketUrl();
    this.client = new WebSocketStreamingClient(wsUrl, {
      autoReconnect: true,
      maxReconnectAttempts: 50,
      reconnectDelay: 1000,
      maxReconnectDelay: 30000,
      heartbeatInterval: 30000,
      connectionTimeout: 10000,
    });
  }

  /**
   * Get singleton instance
   */
  static getInstance(): MissionStatusService {
    if (!instance) {
      instance = new MissionStatusService();
    }
    return instance;
  }

  /**
   * Initialize the service and connect to WebSocket
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.client.connect();
      this.isInitialized = true;
      logger.debug('MissionStatusService initialized');
    } catch (error) {
      logger.error('Failed to initialize MissionStatusService', error);
      throw error;
    }
  }

  /**
   * Shut down the service
   */
  async shutdown(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    await this.client.disconnect();
    this.isInitialized = false;
    logger.debug('MissionStatusService shut down');
  }

  /**
   * Subscribe to mission status updates (async version)
   *
   * Ensures WebSocket connection is established before subscribing.
   * Use this when you need to guarantee the subscription is active before proceeding.
   *
   * @param missionId - Mission ID to subscribe to
   * @param options - Subscription options with event handlers
   * @returns Promise that resolves to unsubscribe function
   */
  async subscribeAsync(missionId: string, executionId: string, options: MissionSubscriptionOptions): Promise<() => void> {
    if (!this.isInitialized) {
      logger.debug('MissionStatusService not initialized, initializing...');
      await this.initialize();
    }

    this.client.subscribeMission(missionId, executionId, options);
    logger.debug('Subscribed to mission', missionId, 'execution', executionId);

    return () => {
      this.client.unsubscribeMission(missionId, executionId, options);
    };
  }

  /**
   * Subscribe to mission status updates (sync version)
   *
   * WARNING: This may drop the subscription message if WebSocket isn't connected yet.
   * Prefer subscribeAsync() for reliable subscriptions.
   *
   * @param missionId - Mission ID to subscribe to
   * @param options - Subscription options with event handlers
   * @returns Unsubscribe function
   */
  subscribe(missionId: string, executionId: string, options: MissionSubscriptionOptions): () => void {
    if (!this.isInitialized) {
      logger.warn('MissionStatusService not initialized, auto-initializing...');
      this.initialize().catch((error) => {
        logger.error('Failed to auto-initialize MissionStatusService', error);
      });
    }

    this.client.subscribeMission(missionId, executionId, options);

    return () => {
      this.client.unsubscribeMission(missionId, executionId, options);
    };
  }

  /**
   * Subscribe again to a run already followed, so the relay replays its open
   * questions (P-095 Amendment S04-7).
   */
  resubscribe(missionId: string, executionId: string): void {
    this.client.resubscribeMission(missionId, executionId);
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.client.getConnectionState();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.client.isConnected();
  }

  /**
   * Check if reconnecting
   */
  isReconnecting(): boolean {
    return this.client.isReconnecting();
  }

  /**
   * Subscribe to connection state changes
   */
  onConnectionStateChange(callback: (state: ConnectionState) => void): () => void {
    return this.client.onStateChange(callback);
  }

  /**
   * Set error handler
   */
  onError(handler: (error: Error) => void): void {
    this.client.setEventHandlers({ onError: handler });
  }

  /**
   * Send HITL response to the server
   * Routes the user's response back to the host executing the mission.
   * Returns false when nothing was sent, so the chat can mark the answer
   * failed at once (phase 4, agion/Agion#2327).
   */
  sendHITLResponse(params: Parameters<WebSocketStreamingClient['sendHITLResponse']>[0]): boolean {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send HITL response: not connected');
      return false;
    }

    return this.client.sendHITLResponse(params);
  }

  /**
   * Send a message the user wrote while the agent works (phase 6,
   * agion/Agion#2327). Returns false when nothing was sent, so the chat can
   * mark the message failed at once, as it does an answer.
   */
  sendUserMessage(message: UserMessageView): boolean {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send user message: not connected');
      return false;
    }

    return this.client.sendUserMessage(message);
  }

  /**
   * Send a reply of the person who holds the run's conversation
   * (agion/Agion#3435). Returns false when nothing was sent, so the operator
   * view can say the reply did not leave.
   */
  sendOperatorMessage(message: OperatorMessageView): boolean {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send operator message: not connected');
      return false;
    }

    return this.client.sendOperatorMessage(message);
  }

  /**
   * Make sure the socket is up, for a sender that has no chat open (the
   * Escalations sections, agion/Agion#3427): initialises the service when
   * nothing has, waits for a reconnecting socket, and opens a closed one.
   * Resolves false when it is not connected within timeoutMs.
   */
  async ensureConnected(timeoutMs: number): Promise<boolean> {
    if (this.isInitialized && this.client.isConnected()) return true;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopWaiting: (() => void) | undefined;
    const timedOut = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), timeoutMs);
    });
    const connected = (async () => {
      if (!this.isInitialized) {
        await this.initialize();
        return true;
      }
      if (this.client.getConnectionState() === 'reconnecting') {
        // The client opens the next socket itself; a second one would race it.
        return new Promise<boolean>((resolve) => {
          stopWaiting = this.client.onStateChange((state) => {
            if (state === 'connected') resolve(true);
          });
        });
      }
      await this.client.connect();
      return true;
    })().catch(() => false);

    try {
      return (await Promise.race([connected, timedOut])) && this.client.isConnected();
    } finally {
      clearTimeout(timer);
      stopWaiting?.();
    }
  }

  /**
   * Send an escalation answer to the run through the relay (agion/Agion#3427,
   * owner ruling 2026-10-25). The frame passes straight to the socket; this
   * keeps no copy and logs none of it. Returns false when nothing was sent.
   */
  sendEscalationAnswer(frame: EscalationAnswerFrame): boolean {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send escalation answer: not connected');
      return false;
    }

    return this.client.sendEscalationAnswer(frame);
  }

  /** Listen to the relay's results of escalation answers; returns the function that stops. */
  onEscalationAnswerResult(listener: (result: EscalationAnswerResult) => void): () => void {
    return this.client.onEscalationAnswerResult(listener);
  }

  /**
   * Send voice user joined notification to the server
   * This triggers the mission.start dispatch to the host after user connects to LiveKit
   */
  sendVoiceUserJoined(params: { missionId: string; executionId: string }): void {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send voice user joined: not connected');
      return;
    }

    this.client.sendVoiceUserJoined(params);
  }

  /**
   * Send mission ready notification to the server
   * This signals that the client has subscribed and is ready to receive mission events.
   * For subscriber-gated missions, this triggers the mission.start dispatch.
   */
  sendMissionReady(params: { missionId: string; executionId: string }): void {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send mission ready: not connected');
      return;
    }

    this.client.sendMissionReady(params);
  }

  /**
   * Send mission stop request (F5 — chat-ui catch-up).
   * Translates to a mission.cancel command on the Edge host that is
   * executing the mission, cancelling the execution context.
   */
  sendMissionStop(params: { missionId: string; executionId: string }): void {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send mission stop: not connected');
      return;
    }

    this.client.sendMissionStop(params);
  }

  /**
   * Send mission pause request.
   * Suspends the execution on the Edge host at the next checkpoint.
   */
  sendMissionPause(params: { missionId: string; executionId: string }): void {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send mission pause: not connected');
      return;
    }

    this.client.sendMissionPause(params);
  }

  /**
   * Send mission resume request.
   * Resumes a previously paused execution on the Edge host.
   */
  sendMissionResume(params: { missionId: string; executionId: string }): void {
    if (!this.isInitialized || !this.client.isConnected()) {
      logger.error('Cannot send mission resume: not connected');
      return;
    }

    this.client.sendMissionResume(params);
  }

  /**
   * Build WebSocket URL from config
   */
  private buildWebSocketUrl(): string {
    // Use dedicated streaming WS URL if configured
    const streamingUrl = config.streamingWsUrl;
    if (streamingUrl) {
      // Convert http(s) to ws(s) if needed
      const wsUrl = streamingUrl.replace(/^http/, 'ws');
      return wsUrl.endsWith('/status') ? wsUrl : `${wsUrl}/status`;
    }

    // Fallback: derive from API URL (config.wsUrl removed in F4 — it pointed
    // at the deleted chat-service WebSocket).
    const baseUrl = config.apiUrl;
    const wsUrl = baseUrl.replace(/^http/, 'ws');

    // If URL already points to streaming service
    if (wsUrl.includes('/v1/streaming')) {
      return wsUrl.endsWith('/status') ? wsUrl : `${wsUrl}/status`;
    }

    // Construct streaming path from base
    return `${wsUrl}/v1/streaming/status`;
  }
}

// Export singleton getter for convenience
export function getMissionStatusService(): MissionStatusService {
  return MissionStatusService.getInstance();
}
